import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, aboutInfo, categories } from '@/mocks/resources';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    try {
      const formData = new FormData(form);
      formData.delete('website_alt');
      const response = await fetch('https://readdy.ai/api/form/d9qq2o4nlsngrm4lm3pg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 4000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormError(serverMsg || '发送失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEO
        title={`关于我们 - ${siteInfo.name}`}
        description={`了解${siteInfo.name}的品牌故事、发展历程和联系方式。${siteInfo.description}`}
        keywords={`关于给力资源库,${siteInfo.name}介绍,资源下载平台,免费资源网站`}
        canonicalUrl="https://geiliziyuan.com/about"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: `关于我们 - ${siteInfo.name}`,
          description: `了解${siteInfo.name}的品牌故事和联系方式`,
          url: 'https://geiliziyuan.com/about',
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/60">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
              <div className="px-4 md:px-6 pb-6">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  关于我们
                </h1>
                <p className="text-sm text-foreground-500">
                  了解{siteInfo.name}的品牌故事与发展历程
                </p>
              </div>
            </div>
          </div>

          {/* Brand Story */}
          <section className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="md:w-1/3 flex-shrink-0">
                <div className="relative w-full aspect-square rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20library%20interior%20with%20warm%20wooden%20shelves%20filled%20with%20colorful%20books%20and%20design%20objects%2C%20cozy%20reading%20nook%20with%20soft%20amber%20lighting%2C%20minimal%20aesthetic%2C%20artistic%20editorial%20photography%20with%20elegant%20composition&width=800&height=800&seq=about-brand-01&orientation=squarish"
                    alt="给力资源库品牌"
                    className="w-full h-full object-cover"
                    title="给力资源库品牌形象"
                  />
                </div>
              </div>
              <div className="md:w-2/3">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  品牌故事
                </h2>
                <div className="text-sm md:text-base text-foreground-600 leading-relaxed space-y-4">
                  {aboutInfo.brandStory.split('\n\n').map((para, idx) => (
                    <p key={idx}>{para}</p>
                  ))}
                </div>
                <div className="mt-6 flex items-center gap-8 flex-wrap">
                  <div className="text-center">
                    <div className="text-2xl font-black text-primary-500">{siteInfo.stats.resources}</div>
                    <div className="text-xs text-foreground-400 mt-1">精品资源</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-black text-primary-500">{siteInfo.stats.users}</div>
                    <div className="text-xs text-foreground-400 mt-1">活跃用户</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-black text-primary-500">{siteInfo.stats.downloads}</div>
                    <div className="text-xs text-foreground-400 mt-1">累计下载</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="bg-background-100 py-12 md:py-16">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
                核心优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[
                  { icon: 'ri-sparkling-line', title: '海量资源', desc: '50,000+份精品资源，覆盖六大核心分类，持续增长中' },
                  { icon: 'ri-check-double-line', title: '品质保障', desc: '每份资源经过人工审核，确保高质量、高实用性' },
                  { icon: 'ri-money-cny-circle-line', title: '完全免费', desc: '坚持免费分享，让优质资源触手可及' },
                  { icon: 'ri-rocket-line', title: '极速更新', desc: '每日更新，紧跟行业趋势，资源始终新鲜' },
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-bold text-foreground-900 mb-1.5">{item.title}</h3>
                      <p className="text-sm text-foreground-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {aboutInfo.reputation.map((item, idx) => (
                <div key={idx} className="p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-200 transition-all duration-300">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: item.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-primary-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    "{item.text}"
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 text-xs font-bold">
                      {item.author[0]}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{item.author}</div>
                      <div className="text-xs text-foreground-400">{item.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="bg-background-100 py-12 md:py-16">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <h3 className="font-heading text-lg font-bold text-foreground-900">联系方式</h3>
                  <ul className="space-y-4">
                    <li className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-400">电子邮箱</div>
                        <div className="text-sm font-medium text-foreground-800">{aboutInfo.contact.email}</div>
                      </div>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                        <i className="ri-wechat-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-400">微信公众号</div>
                        <div className="text-sm font-medium text-foreground-800">{aboutInfo.contact.wechat}</div>
                      </div>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-400">公司地址</div>
                        <div className="text-sm font-medium text-foreground-800">{aboutInfo.contact.address}</div>
                      </div>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                        <i className="ri-time-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-400">工作时间</div>
                        <div className="text-sm font-medium text-foreground-800">{aboutInfo.contact.workHours}</div>
                      </div>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">发送消息</h3>
                  <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-800 outline-none focus:border-primary-300 transition-colors"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-800 outline-none focus:border-primary-300 transition-colors"
                    />
                    <textarea
                      name="message"
                      placeholder="请输入您的留言（最多500字符）"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-800 outline-none focus:border-primary-300 transition-colors resize-none"
                    ></textarea>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <button
                      type="submit"
                      className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-bold text-sm rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 text-center">消息已发送成功，我们会尽快回复！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError || '发送失败，请稍后重试'}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>

          {/* Quick Links */}
          <section className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
              快速导航
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-3 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/50 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${cat.icon} text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-foreground-800">{cat.name}</div>
                    <div className="text-xs text-foreground-400">{cat.count.toLocaleString()} 份</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}